import { InformationCircleIcon } from "@heroicons/react/outline";
import { filter } from "lodash";
import Tooltip from "rc-tooltip";
import React, { useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useFilteredList } from "../../../context/filteredList";
import { useHotspotMakers } from "../../../context/hotspotMakers";
import { Entity } from "../../../utils/constants";
import { locale } from "../../../utils/dates";

const MakerStatisticsTab: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const { hotspotMakersById } = useHotspotMakers();
  const { filteredList } = useFilteredList();

  const currentMaker = useMemo(
    () =>
      props.match.params.id
        ? hotspotMakersById[props.match.params.id]
        : undefined,
    [hotspotMakersById, props.match.params.id]
  );

  const filteredMakersDetails = useMemo(
    () => [
      {
        title: "Total hotspots",
        key: "total_hotspots",
        value: formatNumber(currentMaker?.all_hotspots.total || 0),
        description:
          currentMaker &&
          currentMaker.all_hotspots.total > currentMaker.all_hotspots.online ? (
            <div className="flex items-center text-red-400">
              {(
                ((currentMaker.all_hotspots.total -
                  currentMaker.all_hotspots.online) /
                  currentMaker.all_hotspots.total) *
                100
              ).toFixed(0)}
              % inactive
            </div>
          ) : null,
        tooltip:
          "This sums up all the hotspots from this maker which have been onboarded (added to the blockchain)."
      },
      {
        title: "Activated hotspots",
        key: "activated_hotspots",
        value: formatNumber(currentMaker?.installed_hotspots.total || 0),
        description:
          currentMaker &&
          currentMaker.installed_hotspots.total >
            currentMaker.installed_hotspots.online ? (
            <div className="flex items-center text-red-400">
              {(
                ((currentMaker.installed_hotspots.total -
                  currentMaker.installed_hotspots.online) /
                  currentMaker.installed_hotspots.total) *
                100
              ).toFixed(0)}
              % inactive
            </div>
          ) : null,
        tooltip:
          "An 'activated hotspot' is a hotspot that has already participated in proof of coverage activities."
      },
      {
        title: "Nearby hotspots",
        key: "nearby_hotspots",
        value: formatNumber(
          filter(filteredList[Entity.hotspot], { payer: props.match.params.id })
            .length
        ),
        tooltip:
          "This is the number of hotspots from this maker around the center of the map."
      }
    ],
    [currentMaker, filteredList, props.match.params.id]
  );

  if (!currentMaker) return null;

  return (
    <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <dl className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
        {filteredMakersDetails.map(renderStatisticsCard)}
      </dl>
    </div>
  );
};

export default MakerStatisticsTab;

//
// Utils
//

interface MakersDetails {
  key: string;
  title: string;
  value: string | number;
  description?: JSX.Element | string | null;
  tooltip?: string;
}

const formatNumber = (value: number) => Intl.NumberFormat(locale).format(value);

const renderStatisticsCard = ({
  title,
  key,
  value,
  description,
  tooltip
}: MakersDetails) => (
  <div
    key={key}
    className="relative bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
  >
    <div className="px-4 py-5 sm:p-6">
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate flex items-center justify-between">
        <div className="flex items-center">
          <span className="text-sm mr-1 text-gray-500 truncate dark:text-gray-200">
            {title}
          </span>
          <Tooltip overlay={tooltip}>
            <InformationCircleIcon className="mt-0.5 h-4 w-4 text-gray-400 dark:text-white" />
          </Tooltip>
        </div>
      </dt>

      <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
        <div className="flex flex-row">
          <span className="text-2xl font-medium text-gray-500 truncate dark:text-white mr-1">
            {value}
          </span>
          <p className="text-sm font-medium truncate mt-3">{description}</p>
        </div>
      </dd>
    </div>
  </div>
);
